import React, {
  useState,
  useCallback,
  useMemo,
}                           from 'react';
import styled               from 'styled-components';
import { Create }           from '@material-ui/icons';
import { ISalesPerson }     from '@typings/accountManagement';
import { TDataTableRow }    from '@typings/components';
import { EAppPage }         from '@enums/common';
import { EAccountPage }     from '@enums/accountManagement';
import { useToggle }        from '@utils/useToggle';
import {
  useFetchAllSalesPersons,
  useSaveSalesPerson,
}                           from '@modules/accountManagement/queries/accountQueries';
import { Loader }           from '@components/Loader/Loader';
import {
  LinkButton,
  LinksWrapper,
}                           from '@components/index';
import { SalespersonPage }  from '@modules/accountManagement/pages/SalespersonsPage';

const Wrapper = styled.div`
  padding: 20px 30px 60px;

  @media (min-width: 1280px) {
    padding: 20px 100px 60px;
  }
`;

const AchInfoWrapper = styled.div`
  margin-bottom: 10px;

  & p {
    font-weight: 500;
  }
`;

const StyledCreateIcon = styled(Create)`
  cursor: pointer;
`;

const getTableRows = (
  salesPersons     : ISalesPerson[] = [],
  onAddSalesPerson : (currSalesPerson: ISalesPerson | null) => void,
): TDataTableRow[] => salesPersons.map((salesPerson) => [{
  value: `${salesPerson?.organization ?? ''}`,
}, {
  value: `${salesPerson?.firstName ?? ''}`,
}, {
  value: `${salesPerson?.lastName ?? ''}`,
}, {
  value: `${salesPerson?.phone ?? ''}`,
}, {
  value: `${salesPerson?.mailingAddress ?? ''}`,
}, {
  value: `${salesPerson?.email ?? ''}`,
}, {
  value: (
    <>
      <AchInfoWrapper>
        <p>Bank name:</p>
        {salesPerson?.achInformation?.bankName ?? ''}
      </AchInfoWrapper>

      <AchInfoWrapper>
        <p>Routing Number:</p>
        {salesPerson?.achInformation?.routingNumber ?? ''}
      </AchInfoWrapper>

      <AchInfoWrapper>
        <p>Account Number:</p>
        {salesPerson?.achInformation?.accountNumber ?? ''}
      </AchInfoWrapper>

      <AchInfoWrapper>
        <p>Encoded Authorization Number:</p>
        {salesPerson?.achInformation?.encodedAuthorizationNumber ? '•••••••••' : ''}
      </AchInfoWrapper>
    </>
  ),
}, {
  value: (
    <StyledCreateIcon
      onClick = {(): void => onAddSalesPerson(salesPerson)}
      color   = "secondary"
    />
  ),
  align: 'center',
}]);

interface IProps {
  push: (path: string) => void;
}

export const SalespersonContainer: React.FC<IProps> = ({ push }) => {
  const [currentSalesPerson, setCurrentSalesPerson] = useState<ISalesPerson | null>(null);
  const [allSalesPersons, setAllSalesPersons]       = useState<ISalesPerson[]>([]);
  const [isModalOpen, toggleIsModalOpen]            = useToggle();

  const onAddSalesPersonClose = useCallback(
    (): void => {
      setCurrentSalesPerson(null);
      toggleIsModalOpen();
    },
    [toggleIsModalOpen],
  );

  // #region API INTEGRATION
  const { isLoading } = useFetchAllSalesPersons({
    onSuccess: (data: ISalesPerson[]) => setAllSalesPersons(data),
  });

  const {
    mutate    : saveSalesPerson,
    isLoading : isUpdating,
  } = useSaveSalesPerson({
    onSuccess: (data) => {
      const isEditing   : boolean        = allSalesPersons.some((person) => person.id === data.id);
      const updatedList : ISalesPerson[] = isEditing
        ? allSalesPersons.map((person) => (person.id === data.id ? data : person))
        : [...allSalesPersons, data];

      setAllSalesPersons(updatedList);
      onAddSalesPersonClose();
    },
  });
  // #endregion

  const handleAddSalesPerson = useCallback(
    (currSalesPerson: ISalesPerson | null): void => {
      toggleIsModalOpen();
      setCurrentSalesPerson(currSalesPerson);
    },
    [toggleIsModalOpen],
  );

  const handleSaveSalesperson = useCallback(
    async (data: ISalesPerson): Promise<void> => {
      await saveSalesPerson(data);
    },
    [saveSalesPerson],
  );

  const tableRows = useMemo<TDataTableRow[]>(
    () => getTableRows(allSalesPersons, handleAddSalesPerson),
    [allSalesPersons, handleAddSalesPerson],
  );

  return (
    <Wrapper>
      <LinksWrapper>
        <LinkButton
          label   = "Return to Account Management"
          onClick = {(): void => push(`${EAppPage.AccountManagement}/${EAccountPage.Search}`)}
        />
      </LinksWrapper>

      {isLoading
        ? <Loader />
        : (
          <SalespersonPage
            tableRows             = {tableRows}
            isModalOpen           = {isModalOpen}
            onAddSalesPersonClose = {onAddSalesPersonClose}
            handleSaveSalesperson = {handleSaveSalesperson}
            currentSalesPerson    = {currentSalesPerson}
            handleAddSalesPerson  = {handleAddSalesPerson}
            isUpdating            = {isUpdating}
          />
        )}
    </Wrapper>
  );
};
